import React, { useEffect, useMemo, useState } from 'react';

import { CustomerEvent, CustomerEventType, Funeral, Partner } from '../../types';
import { formatPhone } from '../../lib/phone';
import { PartnerNotifyCard } from './PartnerNotifyCard';
import { DispatchFacts, DispatchRole, formatPartnerAddress } from './notificationTemplates';
import { formatPickupForDispatch } from '../../pricing/pickupLocation';
import { fetchOnFileTypes } from './dispatchDocuments';
import {
  PARTNER_DOC_RULES,
  PartnerDocItem,
  PartnerDocRow,
  partnerDocsStatus,
} from './partnerDocRequirements';
import { usePartnerDocsStatus } from './PartnerDocsPanel';

const REQUIRED_DOC_TYPES = [
  ...new Set(PARTNER_DOC_RULES.flatMap((r) => r.requirements.map((q) => q.documentType))),
];

interface NextOfKinClient {
  name?: string;
  first_name?: string;
  last_name?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
}

interface DispatchNotifyPanelProps {
  record: Funeral;
  deceasedClient:
    | { first_name?: string; last_name?: string; birthdate?: string; weight?: string }
    | null
    | undefined;
  /** Primary client, falling back to authorized — the Ontario dispatch email's next-of-kin block. */
  nextOfKinClient: NextOfKinClient | null | undefined;
  customerEvents: CustomerEvent[];
  createCustomerEvent: (event: Partial<CustomerEvent>) => Promise<unknown>;
  transportPartners: Partner[];
  crematoriumPartners: Partner[];
  dropoffPartners: Partner[];
  isLoadingPartners?: boolean;
  selectedTransport: string;
  onTransportChange: (id: string) => void;
  selectedCrematorium: string;
  onCrematoriumChange: (id: string) => void;
  selectedDropoff: string;
  onDropoffChange: (id: string) => void;
}

const CONTACT_EVENT_TYPES: Record<DispatchRole, CustomerEventType[]> = {
  // A driver dispatch counts however it was made; facilities are contacted by
  // email (or a logged call for the drop-off site).
  transport: [
    CustomerEventType.PHONE_CALL,
    CustomerEventType.TEXT_MESSAGE,
    CustomerEventType.EMAIL,
  ],
  dropoff: [CustomerEventType.EMAIL, CustomerEventType.PHONE_CALL],
  crematorium: [CustomerEventType.EMAIL],
};

export const isRecipientNotified = (
  events: CustomerEvent[],
  role: DispatchRole,
  partnerId: string,
): boolean => {
  if (!partnerId) return false;
  const id = Number(partnerId);
  return events.some(
    (e) =>
      e.recipient_type === role &&
      e.recipient_id === id &&
      CONTACT_EVENT_TYPES[role].includes(e.type as CustomerEventType),
  );
};

/**
 * The dispatch dialog's notification steps, derived from the case's routing:
 * transport always; a drop-off card in Ontario (where the body may be dropped
 * at a facility that is not the crematorium); the crematorium always — but
 * optional in Ontario once a distinct drop-off location is selected.
 */
export const DispatchNotifyPanel: React.FC<DispatchNotifyPanelProps> = ({
  record,
  deceasedClient,
  nextOfKinClient,
  customerEvents,
  createCustomerEvent,
  transportPartners,
  crematoriumPartners,
  dropoffPartners,
  isLoadingPartners,
  selectedTransport,
  onTransportChange,
  selectedCrematorium,
  onCrematoriumChange,
  selectedDropoff,
  onDropoffChange,
}) => {
  const isOntario = record.region === 'ontario';
  const hasDistinctDropoff =
    isOntario && !!selectedDropoff && selectedDropoff !== selectedCrematorium;

  const transportPartner =
    transportPartners.find((p) => p.id?.toString() === selectedTransport) || null;
  const crematoriumPartner =
    crematoriumPartners.find((p) => p.id?.toString() === selectedCrematorium) || null;
  const dropoffPartner = hasDistinctDropoff
    ? dropoffPartners.find((p) => p.id?.toString() === selectedDropoff) || null
    : null;

  // Where the driver takes the body: the drop-off partner if set, else the crematorium.
  const destinationPartner = dropoffPartner ?? crematoriumPartner;

  const nokName =
    nextOfKinClient?.name ||
    `${nextOfKinClient?.first_name || ''} ${nextOfKinClient?.last_name || ''}`.trim();
  const nokAddress = nextOfKinClient
    ? [
        nextOfKinClient.address,
        nextOfKinClient.city,
        nextOfKinClient.province,
        nextOfKinClient.postal_code,
      ]
        .filter(Boolean)
        .join(', ')
    : '';

  const facts: DispatchFacts = {
    funeralId: record.id,
    region: record.region || '',
    deceasedFirstName: deceasedClient?.first_name || '',
    deceasedLastName: deceasedClient?.last_name || '',
    deceasedDob: deceasedClient?.birthdate
      ? String(deceasedClient.birthdate).slice(0, 10)
      : '',
    deceasedWeight: deceasedClient?.weight || '',
    sp3Number: record.sp3_number || '',
    placeOfDeath: formatPickupForDispatch(record),
    transportName: transportPartner?.name || '',
    destinationName: destinationPartner?.name || '',
    destinationAddress: formatPartnerAddress(destinationPartner),
    crematoriumName: crematoriumPartner?.name || '',
    nextOfKinName: nokName,
    nextOfKinPhone: nextOfKinClient?.phone_number
      ? formatPhone(nextOfKinClient.phone_number)
      : '',
    nextOfKinAddress: nokAddress,
  };

  // §4 chips: which documents each selected partner needs and whether they're
  // on file / already sent — computed for the SELECTED (possibly unsaved)
  // partners, with rows from the server and what's on file from the case.
  const { status: savedStatus } = usePartnerDocsStatus(record.id);
  const [onFile, setOnFile] = useState<Record<string, { receivedAt: string | null }>>({});
  useEffect(() => {
    let cancelled = false;
    void fetchOnFileTypes(record.id, REQUIRED_DOC_TYPES).then((d) => {
      if (!cancelled) setOnFile(d);
    });
    return () => {
      cancelled = true;
    };
  }, [record.id, savedStatus]);
  const docItemsFor = useMemo(() => {
    const rows = (savedStatus?.items || [])
      .map((i) => i.row)
      .filter((r): r is PartnerDocRow => !!r);
    const toFacts = (p: Partner | null) =>
      p
        ? [
            {
              id: String(p.id),
              name: p.name,
              email: p.email,
              region: p.region,
              is_crematorium: p.is_crematorium,
              agreement_type: p.agreement_type,
            },
          ]
        : [];
    const status = partnerDocsStatus(
      {
        funeral: {
          region: record.region,
          cremation_status: record.cremation_status,
          dropoff_released_at: record.dropoff_released_at,
          crematorium_partner_id: crematoriumPartner ? String(crematoriumPartner.id) : null,
          dropoff_partner_id: dropoffPartner ? String(dropoffPartner.id) : null,
          transport_partner_id: null,
        },
        partners: [...toFacts(crematoriumPartner), ...toFacts(dropoffPartner)],
        rows,
        onFile,
      },
      new Date().toISOString(),
    );
    return (partnerId: string | undefined, role: DispatchRole): PartnerDocItem[] =>
      partnerId
        ? status.items.filter((i) => i.partnerId === String(partnerId) && i.roles.includes(role))
        : [];
  }, [savedStatus, onFile, record, crematoriumPartner, dropoffPartner]);

  const transportNotified = isRecipientNotified(customerEvents, 'transport', selectedTransport);
  const dropoffNotified = isRecipientNotified(customerEvents, 'dropoff', selectedDropoff);
  const crematoriumNotified = isRecipientNotified(
    customerEvents,
    'crematorium',
    selectedCrematorium,
  );

  const crematoriumStep = isOntario ? 3 : 2;
  const crematoriumTitle =
    isOntario && !hasDistinctDropoff ? 'Crematorium (drop-off location)' : 'Crematorium';

  return (
    <div className="space-y-3">
      <PartnerNotifyCard
        step={1}
        role="transport"
        title="Transport Company"
        notified={transportNotified}
        partners={transportPartners}
        isLoadingPartners={isLoadingPartners}
        selectedId={selectedTransport}
        onSelect={onTransportChange}
        selectLabel="Transport Partner"
        funeralId={record.id}
        facts={facts}
        createCustomerEvent={createCustomerEvent}
      />

      {isOntario && (
        <PartnerNotifyCard
          step={2}
          role="dropoff"
          title="Drop-off Location"
          notified={dropoffNotified}
          partners={dropoffPartners}
          isLoadingPartners={isLoadingPartners}
          selectedId={hasDistinctDropoff ? selectedDropoff : ''}
          onSelect={onDropoffChange}
          selectLabel="Drop-off location"
          emptyOptionLabel="Same as crematorium"
          emptyStateNote="The body will be dropped off at the crematorium — the crematorium notification below covers it."
          funeralId={record.id}
          facts={facts}
          docItems={docItemsFor(dropoffPartner?.id?.toString(), 'dropoff')}
          createCustomerEvent={createCustomerEvent}
        />
      )}

      <PartnerNotifyCard
        step={crematoriumStep}
        role="crematorium"
        title={crematoriumTitle}
        notified={crematoriumNotified}
        partners={crematoriumPartners}
        isLoadingPartners={isLoadingPartners}
        selectedId={selectedCrematorium}
        onSelect={onCrematoriumChange}
        selectLabel="Crematorium"
        funeralId={record.id}
        facts={facts}
        showAgreementAttachment
        docItems={docItemsFor(crematoriumPartner?.id?.toString(), 'crematorium')}
        createCustomerEvent={createCustomerEvent}
      />
    </div>
  );
};
